import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Modern backend is a JavaScript function",
  "description": "Many engineers think server-side code is the scary domain of True Engineers. Something newbies, juniors, and frontend engineers shouldn't touch. They're wrong. Modern backend is a joy. ❤️",
  "published": "2020-09-22T00:00:00.000Z",
  "hero": "./img/screenshot-1600788569261.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Many engineers think server-side code is the scary domain of True Engineers. Something newbies, juniors, and frontend engineers shouldn't touch.`}</p>
    <p>{`They're wrong. Modern backend is a joy. ❤️`}</p>
    <h2 {...{
      "id": "why-engineers-are-afraid-of-backend"
    }}>{`Why engineers are afraid of backend`}</h2>
    <p>{`Backend code scares people because it sounds complex. And serious, and critical, and a bit boring.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Here is a version that works`}<br parentName="p"></br><br parentName="p"></br>{`I really wish that after these 3 hours of hard work I had something better to show than "I am now reasonably certain that when I compare 2 numbers, the comparison is valid"`}<br parentName="p"></br><br parentName="p"></br>{`But that's not how backend life works. `}<a parentName="p" {...{
          "href": "https://t.co/t3FTcHo9Ai"
        }}>{`pic.twitter.com/t3FTcHo9Ai`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1188983047128903680"
      }}>{`October 29, 2019`}</a></blockquote>
    <p>{`Sometimes you fall into the `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/in-2020-what-is-frontend/#backend-of-the-frontend-vs-deep-backend"
      }}>{`Deep Backend`}</a>{` and that's a dark place. You get your telemetry, a stream of data, a large database, and your job is to make sure they match at 99.999% consistency without dataloss.`}</p>
    <p>{`You're fighting against `}<a parentName="p" {...{
        "href": "https://serverlesshandbook.dev/robust-backend-design"
      }}>{`unsolvable paradoxes`}</a>{`, flakey servers, and the boredom of watching graphs wiggle around with no other feedback.`}</p>
    <p>{`🤮`}</p>
    <h3 {...{
      "id": "you-can-do-backend-logic"
    }}>{`You `}{`*`}{`can`}{`*`}{` do backend logic`}</h3>
    <p>{`Most server-side code is not like that. It's JSON bureaucracy.`}</p>
    <p>{`You get data from the client, change a few values, run a couple functions, and save it to the database. Trigger a background process or two to poke 3rd party APIs.`}</p>
    <p>{`Easy peasy my friend. You do this on the frontend every day.`}</p>
    <p>{`Doing it on the backend can be scary.`}</p>
    <p>{`You have to set up a server, make sure it stays up, set up Nginx or Apache reverse proxies, configure a database, make sure `}<em parentName="p">{`that`}</em>{` stays running, configure permissions, muck around with Docker, drop into Unix commands, set up monitoring, configure domains, set up a CDN for static files, deal with provisioning, configure backup servers, dynamic failover, horizontal scaling, vertical scaling, ....`}</p>
    <p>{`Then you're ready to write your code.`}</p>
    <p>{`Publish to reddit and your server dies under load 🤦‍♂️`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/l1J9CiCufdrL0JRlu/giphy-loop.mp4?cid=7cf28b0f9bzo3bxvk226t8jrpjkm5l5hxt9o30xy0tnndepe&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "times-have-changed-my-friend"
    }}>{`Times have changed my friend`}</h2>
    <blockquote>
      <p parentName="blockquote">{`The future is already here, it's just not evenly distributed`}</p>
      <p parentName="blockquote">{`~ William Gibson`}</p>
    </blockquote>
    <p>{`You're standing one foot in the future, one foot in the past. We all are. How we build webapps is changing.`}</p>
    <p>{`Serverless computing splashed on the scene with one promise: What if you `}<em parentName="p">{`didn't`}</em>{` have to do all of the above? What if you could write `}<em parentName="p">{`your`}</em>{` code and nothing else?`}</p>
    <p>{`AWS Lambda launched in November 2014. Netlify in April 2015, Zeit/Vercel in December 2015.`}</p>
    <p>{`All running towards the same goal: `}<em parentName="p">{`You write `}<strong parentName="em">{`your`}</strong>{` code, we make it run`}</em>{`.`}</p>
    <p>{`No more funky configuration, no more servers, no more worrying about scale, no more setting up CDNs and deploy pipelines and parallel deploys and pull request previews and ... you write your code, the platform handles the rest.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/zrxazUScjhxo4/giphy-loop.mp4?cid=7cf28b0fpxr9ohwr2o5x7l7smod1mndegkdm7uk1dpca68ol&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "what-modern-server-side-code-looks-like"
    }}>{`What modern server-side code looks like`}</h2>
    <p>{`Here's what modern backend code looks like at its simplest:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// /api/helloWorld.js

module.exports = (req, res) => {
  return res.status(200).send("Hello world")
}
`}</code></pre>
    <p><inlineCode parentName="p">{`module.exports`}</inlineCode>{` defines exports in Node, `}<inlineCode parentName="p">{`req`}</inlineCode>{` is the request object, `}<inlineCode parentName="p">{`res`}</inlineCode>{` the response. Send back a success status of 200 and a `}<inlineCode parentName="p">{`"Hello world"`}</inlineCode>{` text.`}</p>
    <p><img parentName="p" {...{
        "src": "/412eabb35fe281cb452e125f38b49f56/Vercel-cloud-function-demo2e08a7.gif",
        "alt": "Vercel cloud function demo"
      }}></img></p>
    <p>{`Deploy to Vercel with `}<inlineCode parentName="p">{`vercel`}</inlineCode>{` and you have a hello world function that runs on the server, has its own URL, and scales great. Try mine: `}<a parentName="p" {...{
        "href": "https://hello-world-chi.vercel.app/api/helloWorld"
      }}>{`https://hello-world-chi.vercel.app/api/helloWorld`}</a></p>
    <h3 {...{
      "id": "with-netlify"
    }}>{`With Netlify`}</h3>
    <p>{`You can use the same approach with Netlify. Their cloud functions look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// /functions/helloWorld.js

exports.handler = async (event, context) => {
  return {
    statusCode: 200,
    body: "Hello world",
  }
}
`}</code></pre>
    <p>{`Export a `}<inlineCode parentName="p">{`handler`}</inlineCode>{` method, get a trigger `}<inlineCode parentName="p">{`event`}</inlineCode>{` instead of the `}<inlineCode parentName="p">{`req`}</inlineCode>{` object. Return your response instead of manipulating the `}<inlineCode parentName="p">{`res`}</inlineCode>{` object.`}</p>
    <p>{`Both Vercel and Netlify run cloud functions on AWS Lambda. Vercel uses patterns from the popular `}<a parentName="p" {...{
        "href": "https://expressjs.com"
      }}>{`ExpressJS`}</a>{` framework for its abstraction, Netlify is like using AWS Lambda directly.`}</p>
    <h3 {...{
      "id": "with-aws-lambda"
    }}>{`With AWS Lambda`}</h3>
    <p>{`To use AWS Lambda, you'd write the same JavaScript code and add a configuration file using `}<a parentName="p" {...{
        "href": "https://expressjs.com"
      }}>{`Serverless Framework`}</a>{` or `}<a parentName="p" {...{
        "href": "https://aws.amazon.com/cloudformation/"
      }}>{`CloudFormation`}</a></p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml

service: hello-world
provider:
    name: aws

functions:
    helloWorld:
        handler: ./functions/helloWorld
        events:
            - http:
                    path: hello
                    method: GET
`}</code></pre>
    <p>{`Both use YAML for configuration. I prefer Serverless Framework because it makes common operations easy and lets you drop into full CloudFormation when you want.`}</p>
    <p>{`This file says we're using `}<inlineCode parentName="p">{`aws`}</inlineCode>{` and creating a `}<inlineCode parentName="p">{`hello`}</inlineCode>{` function. Triggered by a GET request, handled by the `}<inlineCode parentName="p">{`/functions/helloWorld`}</inlineCode>{` file.`}</p>
    <p>{`Hit AWS and you get infinite-ish scaling, an API Gateway as a reverse proxy, monitoring, distributed logging, and everything else you need. And the full config is part of your code. 😍`}</p>
    <p>{`More work, more control, but not too much work 🤘`}</p>
    <p>{`What do you think, sound cool? hit reply`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: your code gets automatic SSL config in all 3 examples`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      